/**
 * Mock Vendor API
 *
 * Provides vendor-specific operations (orders, analytics, events) using the mock database.
 * Mirrors the interface of the real vendor API for seamless switching.
 */

import type { ApiResponse } from '../types'
import { mockDb, generateId, timestamp, delay } from './mockDb'
import { success, error } from './client'

// ---------------------------------------------------------------------------
// Types (local to mock — pages use these shapes)
// ---------------------------------------------------------------------------

export interface VendorOrder {
  id: string
  vendorId: string
  challengeTitle: string
  customerName: string
  date: string
  amount: number
  status: 'pending' | 'processing' | 'fulfilled' | 'refunded'
  createdAt: string
  updatedAt: string
}

export interface VendorAnalyticsStats {
  totalViews: number
  totalCompletions: number
  totalRevenue: number
  avgRating: number
  activeStudents: number
  repeatRate: number
  conversionRate: number
  avgCompletionTime: string
}

export interface VendorMonthlyData {
  month: string
  views: number
  completions: number
  revenue: number
}

export interface VendorTopChallenge {
  title: string
  views: number
  completions: number
  revenue: number
  rating: number
}

export interface VendorEvent {
  id: string
  vendorId: string
  name: string
  date: string
  time: string
  location: string
  registrations: number
  capacity: number
  status: 'upcoming' | 'in_progress' | 'completed' | 'cancelled'
  createdAt: string
  updatedAt: string
}

// ---------------------------------------------------------------------------
// Vendor Mock API
// ---------------------------------------------------------------------------

export const vendorApi = {
  // ==========================================================================
  // Orders
  // ==========================================================================

  async getOrders(vendorId: string): Promise<ApiResponse<VendorOrder[]>> {
    await delay(100)
    const orders = mockDb.findWhere<VendorOrder>('vendorOrders', (o) => o.vendorId === vendorId)
    return success(orders.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()))
  },

  async updateOrderStatus(
    orderId: string,
    status: VendorOrder['status']
  ): Promise<ApiResponse<VendorOrder>> {
    await delay(100)
    const updated = mockDb.update<VendorOrder>('vendorOrders', orderId, { status })
    if (!updated) return error('Order not found')
    return success(updated)
  },

  // ==========================================================================
  // Analytics
  // ==========================================================================

  async getAnalytics(vendorId: string): Promise<ApiResponse<VendorAnalyticsStats>> {
    await delay(100)

    const orders = mockDb.findWhere<VendorOrder>('vendorOrders', (o) => o.vendorId === vendorId)
    const totalRevenue = orders
      .filter((o) => o.status !== 'refunded')
      .reduce((sum, o) => sum + o.amount, 0)
    const totalCompletions = orders.filter((o) => o.status === 'fulfilled').length

    const stats: VendorAnalyticsStats = {
      totalViews: Math.round(totalCompletions * 7.3),
      totalCompletions,
      totalRevenue,
      avgRating: 4.6,
      activeStudents: Math.round(totalCompletions * 0.54),
      repeatRate: 34,
      conversionRate: 13.7,
      avgCompletionTime: '2.4 hrs',
    }
    return success(stats)
  },

  async getMonthlyData(vendorId: string): Promise<ApiResponse<VendorMonthlyData[]>> {
    await delay(80)

    const orders = mockDb.findWhere<VendorOrder>('vendorOrders', (o) => o.vendorId === vendorId)

    // Aggregate by month
    const monthMap = new Map<string, { views: number; completions: number; revenue: number }>()
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

    for (const order of orders) {
      const d = new Date(order.date)
      const month = months[d.getMonth()]
      const entry = monthMap.get(month) || { views: 0, completions: 0, revenue: 0 }
      entry.views += 7
      if (order.status === 'fulfilled') entry.completions += 1
      if (order.status !== 'refunded') entry.revenue += order.amount
      monthMap.set(month, entry)
    }

    // If no real data, provide reasonable defaults
    if (monthMap.size === 0) {
      return success([
        { month: 'Oct', views: 3200, completions: 420, revenue: 1580 },
        { month: 'Nov', views: 3800, completions: 510, revenue: 1920 },
        { month: 'Dec', views: 2900, completions: 380, revenue: 1430 },
        { month: 'Jan', views: 4200, completions: 560, revenue: 2100 },
        { month: 'Feb', views: 5100, completions: 680, revenue: 2560 },
        { month: 'Mar', views: 5690, completions: 870, revenue: 2860 },
      ])
    }

    const result: VendorMonthlyData[] = []
    for (const [month, data] of monthMap) {
      result.push({ month, ...data })
    }
    return success(result)
  },

  async getTopChallenges(vendorId: string): Promise<ApiResponse<VendorTopChallenge[]>> {
    await delay(80)

    const orders = mockDb.findWhere<VendorOrder>('vendorOrders', (o) => o.vendorId === vendorId)

    // Group by challenge title
    const challengeMap = new Map<string, { views: number; completions: number; revenue: number }>()
    for (const order of orders) {
      const entry = challengeMap.get(order.challengeTitle) || { views: 0, completions: 0, revenue: 0 }
      entry.views += 7
      if (order.status === 'fulfilled') entry.completions += 1
      if (order.status !== 'refunded') entry.revenue += order.amount
      challengeMap.set(order.challengeTitle, entry)
    }

    const result: VendorTopChallenge[] = []
    for (const [title, data] of challengeMap) {
      result.push({
        title,
        views: data.views * 100,
        completions: data.completions * 100,
        revenue: data.revenue * 10,
        rating: 4.4 + Math.random() * 0.5,
      })
    }
    return success(result.sort((a, b) => b.completions - a.completions).slice(0, 5))
  },

  // ==========================================================================
  // Events
  // ==========================================================================

  async getVendorEvents(vendorId: string): Promise<ApiResponse<VendorEvent[]>> {
    await delay(100)
    const events = mockDb.findWhere<VendorEvent>('vendorEvents', (e) => e.vendorId === vendorId)
    return success(events.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()))
  },

  async createEvent(
    vendorId: string,
    data: Omit<VendorEvent, 'id' | 'vendorId' | 'createdAt' | 'updatedAt'>
  ): Promise<ApiResponse<VendorEvent>> {
    await delay(100)
    const event: VendorEvent = {
      ...data,
      id: generateId(),
      vendorId,
      createdAt: timestamp(),
      updatedAt: timestamp(),
    }
    mockDb.insertWithId('vendorEvents', event)
    return success(event)
  },

  async updateEvent(
    eventId: string,
    data: Partial<VendorEvent>
  ): Promise<ApiResponse<VendorEvent>> {
    await delay(100)
    const updated = mockDb.update<VendorEvent>('vendorEvents', eventId, data)
    if (!updated) return error('Event not found')
    return success(updated)
  },

  async deleteEvent(eventId: string): Promise<ApiResponse<void>> {
    await delay(100)
    const deleted = mockDb.delete('vendorEvents', eventId)
    if (!deleted) return error('Event not found')
    return success(undefined as unknown as void)
  },
}
